import { useEffect, useState, useCallback } from "react";
import api, { downloadPdf, mediaUrl } from "@/lib/apiClient";
import { waLink } from "@/lib/modules";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Target, Plus, Trash2, Check, Pencil, MessageCircle, Clock, AlertTriangle,
  ListTodo, Settings2, User, Users2, FileDown, Bell, History, LayoutGrid, List, Send,
  Paperclip, Repeat, Crown,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [
  { value: "baixa", label: "Baixa", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  { value: "media", label: "Média", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  { value: "alta", label: "Alta", cls: "bg-red-100 text-red-700 border-red-200" },
];
const STATUSES = [
  { value: "pendente", label: "Pendente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "andamento", label: "Em andamento", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  { value: "concluido", label: "Concluído", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "cancelado", label: "Cancelado", cls: "bg-slate-100 text-slate-500 border-slate-200" },
];
const priMeta = (v) => PRIORITIES.find((p) => p.value === v) || PRIORITIES[1];
const stMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];
const RECURRENCES = [
  { value: "none", label: "Não repete" },
  { value: "weekly", label: "Toda semana" },
  { value: "monthly", label: "Todo mês" },
];
const recLabel = (v) => (v === "weekly" ? "Semanal" : v === "monthly" ? "Mensal" : "");

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return s; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

function CategoriesDialog({ categories, onSaved }) {
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState(categories);
  useEffect(() => { if (open) setCats(categories); }, [open, categories]);
  const save = async () => {
    try {
      await api.put("/settings", { commitment_categories: cats.filter((c) => c.trim()) });
      toast.success("Categorias salvas");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="categories-config-button"><Settings2 className="h-4 w-4" /> Categorias</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Categorias de compromisso</DialogTitle>
          <DialogDescription>Adicione ou remova as categorias usadas pela liderança.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {cats.map((c, i) => (
            <div key={i} className="flex gap-2">
              <Input value={c} onChange={(e) => setCats(cats.map((x, j) => j === i ? e.target.value : x))} data-testid={`category-input-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => setCats(cats.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setCats([...cats, ""])} data-testid="add-category-button"><Plus className="h-4 w-4" /> Nova categoria</Button>
        </div>
        <DialogFooter><Button onClick={save} className="gap-2" data-testid="save-categories"><Check className="h-4 w-4" /> Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ commitment, open, setOpen, onSaved }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const notes = commitment?.notes || [];
  useEffect(() => { if (open) { setText(""); setFile(null); } }, [open]);
  const add = async () => {
    if (!text.trim() && !file) return toast.error("Adicione um texto ou um anexo");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      if (file) fd.append("file", file);
      await api.post(`/commitments/${commitment.id}/notes`, fd);
      setText(""); setFile(null); onSaved();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  const isImg = (ct) => (ct || "").startsWith("image/");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Diário de andamento</DialogTitle>
          <DialogDescription>{commitment?.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Registre um retorno, conversa ou progresso…" data-testid="note-input" />
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer hover:text-foreground" data-testid="note-attach-label">
                <Paperclip className="h-4 w-4" /> {file ? file.name : "Anexar foto/documento"}
                <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="note-file-input" />
              </label>
              {file && <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setFile(null)}>remover</Button>}
              <Button onClick={add} disabled={saving} className="self-end gap-1.5 ml-auto" data-testid="add-note-button"><Send className="h-4 w-4" /> Registrar</Button>
            </div>
          </div>
          <div className="space-y-2 pt-2 border-t border-border" data-testid="notes-timeline">
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Nenhum registro ainda. Adicione o primeiro andamento acima.</p>
            ) : notes.map((n) => (
              <div key={n.id} className="rounded-lg bg-secondary/50 p-3" data-testid={`note-${n.id}`}>
                {n.text && <p className="text-sm whitespace-pre-wrap">{n.text}</p>}
                {n.attachment && (
                  isImg(n.attachment.content_type) ? (
                    <a href={mediaUrl(n.attachment.path)} target="_blank" rel="noopener noreferrer" data-testid={`note-attach-${n.id}`}>
                      <img src={mediaUrl(n.attachment.path)} alt={n.attachment.name} className="mt-2 max-h-40 rounded-lg border border-border" />
                    </a>
                  ) : (
                    <a href={mediaUrl(n.attachment.path)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid={`note-attach-${n.id}`}>
                      <Paperclip className="h-3.5 w-3.5" /> {n.attachment.name}
                    </a>
                  )
                )}
                <p className="text-xs text-muted-foreground mt-1.5">{n.author_name} · {fmtDateTime(n.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RemindersDialog({ open, setOpen }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (open) api.get("/commitments/reminders").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [open]);
  const send = (it) => {
    const link = waLink(it.reminder_phone, it.reminder_message);
    if (!link) return toast.error("Sem telefone para este compromisso");
    window.open(link, "_blank");
  };
  const sendAll = () => {
    const withPhone = items.filter((i) => i.reminder_phone);
    if (withPhone.length === 0) return toast.error("Nenhum lembrete com telefone");
    withPhone.forEach((it, i) => { const l = waLink(it.reminder_phone, it.reminder_message); if (l) setTimeout(() => window.open(l, "_blank"), i * 400); });
    toast.success(`Abrindo ${withPhone.length} lembrete(s) no WhatsApp… permita pop-ups.`);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="reminders-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-primary" /> Lembretes</DialogTitle>
          <DialogDescription>Compromissos atrasados ou que vencem nos próximos 7 dias. Envie um lembrete pelo WhatsApp.</DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="reminders-empty">Nenhum lembrete no momento. Tudo em dia! 🙌</p>
        ) : (
          <div className="space-y-2">
            <Button variant="outline" size="sm" className="gap-1.5 w-full" onClick={sendAll} data-testid="send-all-reminders"><Send className="h-4 w-4" /> Enviar todos os lembretes</Button>
            {items.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3" data-testid={`reminder-${it.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{it.title}</p>
                  <p className={`text-xs ${it.overdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                    {it.person_name || it.assignee_name} · {fmtDate(it.due_date)}{it.overdue ? " (atrasado)" : ""}
                  </p>
                </div>
                <Button size="sm" variant="ghost" className="gap-1.5 text-[hsl(var(--emerald))] shrink-0" disabled={!it.reminder_phone} onClick={() => send(it)} data-testid={`send-reminder-${it.id}`}>
                  <MessageCircle className="h-4 w-4" /> Lembrar
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2">Os lembretes abrem o WhatsApp com a mensagem pronta — o envio é feito por você (não é automático em segundo plano).</p>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ open, setOpen, assignees, groups }) {
  const [leaderId, setLeaderId] = useState("all");
  const [groupId, setGroupId] = useState("all");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setLeaderId("all"); setGroupId("all"); } }, [open]);
  const download = async () => {
    setBusy(true);
    try {
      const params = {};
      if (leaderId !== "all") params.leader_id = leaderId;
      if (groupId !== "all") params.group_id = groupId;
      await downloadPdf("/commitments/report", params, "metas-compromissos.pdf");
      setOpen(false);
    } catch { toast.error("Erro ao gerar PDF"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileDown className="h-5 w-5 text-primary" /> Relatório de Compromissos</DialogTitle>
          <DialogDescription>Gere um PDF com o que foi combinado e o cumprimento. Filtre por líder e/ou grupo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Líder / Responsável</Label>
            <Select value={leaderId} onValueChange={setLeaderId}>
              <SelectTrigger className="mt-1.5" data-testid="report-leader-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Grupo (PGD)</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger className="mt-1.5" data-testid="report-group-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={download} disabled={busy} className="gap-2" data-testid="download-report"><FileDown className="h-4 w-4" /> {busy ? "Gerando…" : "Baixar PDF"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverviewTable({ rows, onRowClick }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-muted-foreground text-center py-6">Sem dados.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2 pr-3 font-medium">Nome</th>
            <th className="py-2 px-2 font-medium text-center">Total</th>
            <th className="py-2 px-2 font-medium text-center">Pend.</th>
            <th className="py-2 px-2 font-medium text-center">Andam.</th>
            <th className="py-2 px-2 font-medium text-center">Concl.</th>
            <th className="py-2 pl-2 font-medium text-center">Atrasados</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const clickable = onRowClick && r.id;
            return (
              <tr
                key={i}
                className={`border-b border-border/50 ${clickable ? "cursor-pointer hover:bg-secondary" : ""}`}
                onClick={clickable ? () => onRowClick(r) : undefined}
                data-testid={`overview-row-${i}`}
              >
                <td className="py-2 pr-3">
                  <div className={`font-medium ${clickable ? "text-primary" : ""}`}>{r.name}</div>
                  {r.role && <div className="text-[11px] text-muted-foreground">{r.role}</div>}
                </td>
                <td className="py-2 px-2 text-center font-semibold">{r.total}</td>
                <td className="py-2 px-2 text-center text-amber-700">{r.pendentes}</td>
                <td className="py-2 px-2 text-center text-sky-700">{r.andamento}</td>
                <td className="py-2 px-2 text-center text-emerald-700">{r.concluidos}</td>
                <td className="py-2 pl-2 text-center">
                  {r.atrasados > 0 ? <span className="font-bold text-red-600">{r.atrasados}</span> : <span className="text-muted-foreground">0</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PastorPanelDialog({ open, setOpen }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("lider");
  const [drill, setDrill] = useState(null);
  const [drillItems, setDrillItems] = useState([]);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (open) {
      setTab("lider"); setDrill(null); setDrillItems([]);
      api.get("/commitments/overview").then((r) => setData(r.data)).catch(() => setData(null));
    }
  }, [open]);

  const openLeader = (row) => {
    setDrill(row);
    api.get("/commitments", { params: { assignee_id: row.id } }).then((r) => setDrillItems(r.data)).catch(() => setDrillItems([]));
  };
  const sendDigest = async () => {
    setSending(true);
    try {
      const r = await api.post("/commitments/weekly-digest");
      toast.success(`Resumo enviado (${r.data.overdue} atrasado(s)) para os pastores/coordenadores.`);
    } catch { toast.error("Erro ao enviar resumo"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="pastor-panel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crown className="h-5 w-5 text-accent" /> Painel do Pastor</DialogTitle>
          <DialogDescription>Visão geral dos compromissos de toda a liderança, por líder e por supervisor.</DialogDescription>
        </DialogHeader>

        {drill ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <button onClick={() => setDrill(null)} className="text-sm text-primary hover:underline" data-testid="drill-back">← Voltar ao painel</button>
                <h3 className="font-semibold mt-1">Compromissos de {drill.name}</h3>
              </div>
              <Badge variant="secondary">{drillItems.length}</Badge>
            </div>
            {drillItems.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="drill-empty">Nenhum compromisso para esta pessoa.</p>
            ) : (
              <div className="space-y-2" data-testid="drill-list">
                {drillItems.map((c) => {
                  const sm = stMeta(c.status);
                  return (
                    <div key={c.id} className={`rounded-lg border p-3 ${c.overdue ? "border-red-300" : "border-border"}`} data-testid={`drill-item-${c.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{c.title}</span>
                        {c.category && <Badge variant="outline" className="text-[10px]">{c.category}</Badge>}
                        <Badge variant="outline" className={`text-[10px] ${sm.cls}`}>{sm.label}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap">
                        {c.person_name && <span>{c.person_name}</span>}
                        {c.group_name && <span>· {c.group_name}</span>}
                        {c.due_date && <span className={c.overdue ? "text-red-600 font-medium" : ""}>{fmtDate(c.due_date)}{c.overdue ? " (atrasado)" : ""}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            {data && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid="overview-totals">
                <div className="rounded-lg border border-border p-2 text-center"><div className="text-xl font-bold">{data.totals.total}</div><div className="text-[11px] text-muted-foreground">Total</div></div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-center"><div className="text-xl font-bold text-amber-700">{data.totals.pendentes}</div><div className="text-[11px] text-muted-foreground">Pendentes</div></div>
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-2 text-center"><div className="text-xl font-bold text-sky-700">{data.totals.andamento}</div><div className="text-[11px] text-muted-foreground">Andamento</div></div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-center"><div className="text-xl font-bold text-emerald-700">{data.totals.concluidos}</div><div className="text-[11px] text-muted-foreground">Concluídos</div></div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-center"><div className="text-xl font-bold text-red-700">{data.totals.atrasados}</div><div className="text-[11px] text-muted-foreground">Atrasados</div></div>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="overview-tabs">
                  <TabsTrigger value="lider" data-testid="overview-tab-lider">Por líder</TabsTrigger>
                  <TabsTrigger value="supervisor" data-testid="overview-tab-supervisor">Por supervisor</TabsTrigger>
                </TabsList>
              </Tabs>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={sendDigest} disabled={sending} data-testid="send-digest-button">
                <Bell className="h-4 w-4" /> {sending ? "Enviando…" : "Enviar resumo agora"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Toque em um líder para ver todos os compromissos dele. O resumo é enviado automaticamente toda segunda-feira.</p>
            <div className="mt-2">
              {tab === "lider"
                ? <OverviewTable rows={data?.by_leader} onRowClick={openLeader} />
                : <OverviewTable rows={data?.by_supervisor} />}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CommitmentDialog({ open, setOpen, editing, categories, assignees, groups, onSaved }) {
  const empty = { title: "", description: "", category: categories[0] || "", priority: "media", status: "pendente", due_date: "", assignee_id: "", group_id: "", person_member_id: "", person_name: "", recurrence: "none" };
  const [form, setForm] = useState(empty);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        title: editing.title || "", description: editing.description || "", category: editing.category || (categories[0] || ""),
        priority: editing.priority || "media", status: editing.status || "pendente", due_date: editing.due_date || "",
        assignee_id: editing.assignee_id || "", group_id: editing.group_id || "", person_member_id: editing.person_member_id || "",
        person_name: editing.person_name || "", recurrence: editing.recurrence || "none",
      } : { ...empty });
    }
    // eslint-disable-next-line
  }, [open, editing]);

  useEffect(() => {
    if (form.group_id) api.get("/members", { params: { group_id: form.group_id } }).then((r) => setMembers(r.data)).catch(() => setMembers([]));
    else setMembers([]);
  }, [form.group_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Informe um título");
    const payload = {
      ...form,
      assignee_id: form.assignee_id || null,
      group_id: form.group_id || null,
      person_member_id: form.person_member_id || null,
      due_date: form.due_date || null,
    };
    try {
      if (editing) await api.put(`/commitments/${editing.id}`, payload);
      else await api.post("/commitments", payload);
      toast.success(editing ? "Compromisso atualizado" : "Compromisso criado");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar compromisso" : "Nova meta / compromisso"}</DialogTitle>
          <DialogDescription>Defina o responsável, o prazo e sobre quem/qual grupo é o acordo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Título *</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex: Visitar a família Souza" className="mt-1.5" data-testid="commitment-title-input" />
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Detalhes do acordo…" className="mt-1.5" data-testid="commitment-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="commitment-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridade</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1.5" data-testid="commitment-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Prazo</Label>
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5" data-testid="commitment-due-input" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1.5" data-testid="commitment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Responsável</Label>
            <Select value={form.assignee_id || "self"} onValueChange={(v) => set("assignee_id", v === "self" ? "" : v)}>
              <SelectTrigger className="mt-1.5" data-testid="commitment-assignee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="self">Eu mesmo(a)</SelectItem>
                {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Repeat className="h-3.5 w-3.5" /> Repetição</Label>
            <Select value={form.recurrence} onValueChange={(v) => set("recurrence", v)}>
              <SelectTrigger className="mt-1.5" data-testid="commitment-recurrence-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RECURRENCES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            {form.recurrence !== "none" && <p className="text-xs text-muted-foreground mt-1">Ao concluir, o próximo compromisso é criado automaticamente com o novo prazo.</p>}
          </div>
          <div>
            <Label>Grupo (PGD) relacionado</Label>
            <Select value={form.group_id || "none"} onValueChange={(v) => { set("group_id", v === "none" ? "" : v); set("person_member_id", ""); }}>
              <SelectTrigger className="mt-1.5" data-testid="commitment-group-select"><SelectValue placeholder="Nenhum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.group_id && (
            <div>
              <Label>Sobre qual participante</Label>
              <Select value={form.person_member_id || "none"} onValueChange={(v) => set("person_member_id", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="commitment-person-select"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={save} className="gap-2" data-testid="save-commitment"><Check className="h-4 w-4" /> Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryChip({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className={`flex items-center gap-2 rounded-xl border px-4 py-3 ${tone}`} data-testid={testid}>
      <Icon className="h-5 w-5" />
      <div><div className="text-2xl font-bold leading-none">{value}</div><div className="text-xs mt-0.5">{label}</div></div>
    </div>
  );
}

function CommitmentCard({ c, isAdmin, onStatus, onEdit, onDelete, onCobrar, onHistory }) {
  const pm = priMeta(c.priority), sm = stMeta(c.status);
  const phone = c.person_phone || c.assignee_phone;
  const noteCount = (c.notes || []).length;
  return (
    <Card className={`p-5 ${c.overdue ? "border-red-300" : ""}`} data-testid={`commitment-card-${c.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-base">{c.title}</h3>
          {c.category && <Badge variant="outline" className="text-xs">{c.category}</Badge>}
          <Badge variant="outline" className={`text-xs ${pm.cls}`}>{pm.label}</Badge>
          <Badge variant="outline" className={`text-xs ${sm.cls}`}>{sm.label}</Badge>
          {c.recurrence && c.recurrence !== "none" && (
            <Badge variant="outline" className="text-xs gap-1 bg-violet-50 text-violet-700 border-violet-200"><Repeat className="h-3 w-3" /> {recLabel(c.recurrence)}</Badge>
          )}
        </div>
        {c.description && <p className="text-sm text-muted-foreground mt-1.5">{c.description}</p>}
        <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-muted-foreground mt-2">
          <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {c.assignee_name || "—"}</span>
          {c.person_name && <span className="inline-flex items-center gap-1"><Users2 className="h-3.5 w-3.5" /> {c.person_name}</span>}
          {c.group_name && <span>· {c.group_name}</span>}
          {c.due_date && <span className={`inline-flex items-center gap-1 ${c.overdue ? "text-red-600 font-medium" : ""}`}><Clock className="h-3.5 w-3.5" /> {fmtDate(c.due_date)}{c.overdue ? " (atrasado)" : ""}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-4 pt-3 border-t border-border">
        {c.status !== "andamento" && c.status !== "concluido" && (
          <Button variant="outline" size="sm" onClick={() => onStatus(c, "andamento")} data-testid={`start-${c.id}`}>Iniciar</Button>
        )}
        {c.status !== "concluido" && (
          <Button variant="outline" size="sm" className="gap-1.5 text-emerald-700 border-emerald-200" onClick={() => onStatus(c, "concluido")} data-testid={`complete-${c.id}`}><Check className="h-3.5 w-3.5" /> Concluir</Button>
        )}
        {c.status === "concluido" && (
          <Button variant="outline" size="sm" onClick={() => onStatus(c, "pendente")} data-testid={`reopen-${c.id}`}>Reabrir</Button>
        )}
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => onHistory(c)} data-testid={`history-${c.id}`}>
          <History className="h-3.5 w-3.5" /> Diário{noteCount > 0 ? ` (${noteCount})` : ""}
        </Button>
        {phone && (
          <Button variant="ghost" size="sm" className="gap-1.5 text-[hsl(var(--emerald))]" onClick={() => onCobrar(c)} data-testid={`wa-${c.id}`}><MessageCircle className="h-3.5 w-3.5" /> Cobrar</Button>
        )}
        <div className="ml-auto flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(c)} data-testid={`edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
          {(isAdmin || c.is_created) && (
            <Button variant="ghost" size="icon" onClick={() => onDelete(c)} data-testid={`delete-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          )}
        </div>
      </div>
    </Card>
  );
}

const KANBAN_COLS = [
  { key: "pendente", label: "Pendente", cls: "border-amber-200" },
  { key: "andamento", label: "Em andamento", cls: "border-sky-200" },
  { key: "concluido", label: "Concluído", cls: "border-emerald-200" },
];

function KanbanView({ items, onStatus, onEdit, onHistory }) {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const drop = (colKey) => {
    const c = items.find((x) => x.id === dragId);
    if (c && c.status !== colKey) onStatus(c, colKey);
    setDragId(null); setOver(null);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kanban-board">
      {KANBAN_COLS.map((col) => {
        const colItems = items.filter((c) => c.status === col.key);
        return (
          <div
            key={col.key}
            onDragOver={(e) => { e.preventDefault(); setOver(col.key); }}
            onDragLeave={() => setOver((o) => o === col.key ? null : o)}
            onDrop={() => drop(col.key)}
            className={`rounded-xl border ${col.cls} bg-secondary/30 p-3 min-h-[200px] transition-colors ${over === col.key ? "ring-2 ring-primary/40 bg-primary/5" : ""}`}
            data-testid={`kanban-col-${col.key}`}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-sm font-semibold">{col.label}</span>
              <Badge variant="secondary" className="text-xs">{colItems.length}</Badge>
            </div>
            <div className="space-y-2">
              {colItems.map((c) => {
                const pm = priMeta(c.priority);
                return (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={() => setDragId(c.id)}
                    onDragEnd={() => { setDragId(null); setOver(null); }}
                    className={`rounded-lg bg-background border p-3 cursor-grab active:cursor-grabbing ${c.overdue ? "border-red-300" : "border-border"} ${dragId === c.id ? "opacity-50" : ""}`}
                    data-testid={`kanban-card-${c.id}`}
                  >
                    <p className="text-sm font-medium leading-snug">{c.title}</p>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                      {c.category && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{c.category}</Badge>}
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${pm.cls}`}>{pm.label}</Badge>
                      {c.recurrence && c.recurrence !== "none" && <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-0.5 bg-violet-50 text-violet-700 border-violet-200"><Repeat className="h-2.5 w-2.5" /> {recLabel(c.recurrence)}</Badge>}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[11px] text-muted-foreground truncate">{c.person_name || c.assignee_name || ""}</span>
                      {c.due_date && <span className={`text-[11px] ${c.overdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>{fmtDate(c.due_date)}</span>}
                    </div>
                    <div className="flex gap-1 mt-2 pt-2 border-t border-border/70">
                      <Button variant="ghost" size="sm" className="h-7 px-2 gap-1 text-xs" onClick={() => onHistory(c)} data-testid={`kanban-history-${c.id}`}><History className="h-3 w-3" /> Diário</Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs ml-auto" onClick={() => onEdit(c)} data-testid={`kanban-edit-${c.id}`}><Pencil className="h-3 w-3" /></Button>
                    </div>
                  </div>
                );
              })}
              {colItems.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Arraste para cá</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Metas() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin || user?.role === "coordenador";
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tab, setTab] = useState("minhas");
  const [statusFilter, setStatusFilter] = useState("abertos");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [view, setView] = useState("lista");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [pastorOpen, setPastorOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/commitments").then((r) => setItems(r.data)).catch(() => {});
    api.get("/commitments/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setCategories(r.data.commitment_categories || [])).catch(() => {});
    api.get("/commitments/assignees").then((r) => setAssignees(r.data)).catch(() => {});
    api.get("/groups", { params: isAdmin ? {} : { mine: true } }).then((r) => setGroups(r.data)).catch(() => {});
  }, [load, isAdmin]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setDialogOpen(true); };
  const openHistory = (c) => { setHistoryItem(c); setHistoryOpen(true); };

  // keep the open history dialog in sync after reloads (e.g. new note added)
  useEffect(() => {
    if (historyItem) {
      const fresh = items.find((x) => x.id === historyItem.id);
      if (fresh && fresh !== historyItem) setHistoryItem(fresh);
    }
    // eslint-disable-next-line
  }, [items]);

  const changeStatus = async (c, status) => {
    try { await api.patch(`/commitments/${c.id}/status`, { status }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Excluir o compromisso "${c.title}"?`)) return;
    try { await api.delete(`/commitments/${c.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const cobrar = (c) => {
    const phone = c.person_phone || c.assignee_phone;
    const who = c.person_name || c.assignee_name || "";
    const link = waLink(phone, `Olá ${who ? who.split(" ")[0] : ""}! Sobre o combinado: ${c.title}. Como está o andamento?`);
    if (!link) return toast.error("Sem telefone para cobrança");
    window.open(link, "_blank");
  };

  const byTab = items.filter((c) => tab === "todas" ? true : tab === "minhas" ? c.is_mine : (c.is_created || (!c.is_mine)))
    .filter((c) => categoryFilter === "all" ? true : c.category === categoryFilter);
  const filtered = byTab.filter((c) =>
    statusFilter === "todos" ? true :
    statusFilter === "abertos" ? (c.status === "pendente" || c.status === "andamento") :
    c.status === statusFilter
  );

  return (
    <div className="space-y-8 animate-fade-up pb-24" data-testid="metas-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
            <Target className="h-8 w-8 text-primary" /> Metas & Compromissos
          </h1>
          <p className="text-muted-foreground mt-1">Acompanhe os acordos assumidos com a liderança e os participantes — sem perder nada de vista.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && <Button variant="outline" className="gap-2 border-accent/40 text-accent hover:text-accent" onClick={() => setPastorOpen(true)} data-testid="open-pastor-panel-button"><Crown className="h-4 w-4" /> Painel do Pastor</Button>}
          <Button variant="outline" className="gap-2" onClick={() => setReportOpen(true)} data-testid="open-report-button"><FileDown className="h-4 w-4" /> Relatório PDF</Button>
          <Button variant="outline" className="gap-2" onClick={() => setRemindersOpen(true)} data-testid="open-reminders-button"><Bell className="h-4 w-4" /> Lembretes</Button>
          {isAdmin && <CategoriesDialog categories={categories} onSaved={() => api.get("/settings").then((r) => setCategories(r.data.commitment_categories || []))} />}
          <Button className="gap-2" onClick={openNew} data-testid="new-commitment-button"><Plus className="h-4 w-4" /> Nova meta</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="commitments-summary">
          <SummaryChip icon={ListTodo} label="Minhas em aberto" value={summary.minhas} tone="bg-primary/5 border-primary/20 text-primary" testid="summary-minhas" />
          <SummaryChip icon={Clock} label="Pendentes (escopo)" value={summary.pendentes} tone="bg-amber-50 border-amber-200 text-amber-700" testid="summary-pendentes" />
          <SummaryChip icon={AlertTriangle} label="Atrasados" value={summary.atrasados} tone="bg-red-50 border-red-200 text-red-700" testid="summary-atrasados" />
          <SummaryChip icon={Clock} label="Vencendo (7 dias)" value={summary.vencendo} tone="bg-sky-50 border-sky-200 text-sky-700" testid="summary-vencendo" />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="metas-tabs">
            <TabsTrigger value="minhas" data-testid="tab-minhas">Minhas</TabsTrigger>
            <TabsTrigger value="equipe" data-testid="tab-equipe">Da equipe</TabsTrigger>
            <TabsTrigger value="todas" data-testid="tab-todas">Todas</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40" data-testid="category-filter-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas categorias</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-lg border border-border p-0.5" data-testid="view-toggle">
            <Button variant={view === "lista" ? "secondary" : "ghost"} size="sm" className="gap-1.5 h-8" onClick={() => setView("lista")} data-testid="view-lista"><List className="h-4 w-4" /> Lista</Button>
            <Button variant={view === "kanban" ? "secondary" : "ghost"} size="sm" className="gap-1.5 h-8" onClick={() => setView("kanban")} data-testid="view-kanban"><LayoutGrid className="h-4 w-4" /> Kanban</Button>
          </div>
          {view === "lista" && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="abertos">Em aberto</SelectItem>
                <SelectItem value="todos">Todos os status</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {view === "kanban" ? (
        <KanbanView items={byTab} onStatus={changeStatus} onEdit={openEdit} onHistory={openHistory} />
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="metas-empty">
          Nenhum compromisso por aqui. Clique em <span className="font-medium">"Nova meta"</span> para começar.
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="commitments-list">
          {filtered.map((c) => (
            <CommitmentCard key={c.id} c={c} isAdmin={isAdmin} onStatus={changeStatus} onEdit={openEdit} onDelete={remove} onCobrar={cobrar} onHistory={openHistory} />
          ))}
        </div>
      )}

      <CommitmentDialog open={dialogOpen} setOpen={setDialogOpen} editing={editing} categories={categories} assignees={assignees} groups={groups} onSaved={load} />
      <HistoryDialog commitment={historyItem} open={historyOpen} setOpen={setHistoryOpen} onSaved={load} />
      <RemindersDialog open={remindersOpen} setOpen={setRemindersOpen} />
      <ReportDialog open={reportOpen} setOpen={setReportOpen} assignees={assignees} groups={groups} />
      <PastorPanelDialog open={pastorOpen} setOpen={setPastorOpen} />
    </div>
  );
}
